import React, { useEffect, useRef, useState } from "react";
import useClassroom from "../useClassroom";
import useAthenaAPIClient from "../../../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import { LegacyLoader } from "../../../../../components/Loader/Loader";
import GroupNamePopoverMenu from "../GroupNamePopoverMenu/GroupNamePopoverMenu";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import Input from "../../../../../design-system-components/Input/Input";

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { commonMessages } from "../../../../../utils/messages";
import useOnlineStatus from "../../../../../hooks/useOnlineStatus";

import "./ClassroomName.scss";

interface ClassroomNameProps {
    id: string;
    className?: string;
    name: string;
}

const ClassroomName = ({ id, className, name }: ClassroomNameProps) => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();

    const inputRef = useRef<HTMLInputElement>(null);

    const [validatedName, setValidatedName] = useState<string>(name);
    const [classroomName, setClassroomName] = useState<string>(name);

    const { updateDashboardData } = useClassroom();
    const athenaAPIClient = useAthenaAPIClient();

    const [editStatus, setEditStatus] = useState<"idle" | "edit" | "saving">(
        "idle"
    );

    const editClassroomName = async (id: string, name: string) => {
        // set state to "saving"
        setEditStatus("saving");

        // Construct body to send to the API
        const body = { name };

        try {
            //Update classroom data
            await athenaAPIClient.updateClassroom(id, body);
            try {
                await updateDashboardData();
                setValidatedName(classroomName);
                // Set state to idle
                setEditStatus("idle");
            } catch (error) {
                setEditStatus("idle");
            }
        } catch (error) {
            setEditStatus("idle");
            setClassroomName(validatedName);
        }
    };

    const cancelEdit = () => {
        setClassroomName(validatedName);
        setEditStatus("idle");
    };

    useEffect(() => {
        if (editStatus === "edit") {
            inputRef.current?.focus();
        }
    }, [editStatus, name]);

    return (
        <div className={`classroom-name ${className ?? ""}`}>
            {editStatus === "idle" && (
                <div className="classroom-name--idle">
                    <h1>
                        <span className="classroom-name__name">
                            {validatedName}
                        </span>
                    </h1>
                    {isOnline && (
                        <GroupNamePopoverMenu
                            label={intl.formatMessage({
                                id: "dashboard-settings",
                                defaultMessage: "Settings",
                            })}
                            onAction={() => setEditStatus("edit")}
                        >
                            <GroupNamePopoverMenu.Item>
                                {intl.formatMessage({
                                    id: "dashboard-renameclass",
                                    defaultMessage: "Rename class",
                                })}
                            </GroupNamePopoverMenu.Item>
                        </GroupNamePopoverMenu>
                    )}
                </div>
            )}
            {editStatus === "edit" && (
                <div className="classroom-name--edit">
                    <VisuallyHidden>
                        <label htmlFor="rename-class">
                            {intl.formatMessage(messages.renameClassLabel, {
                                classroomName: classroomName,
                            })}
                        </label>
                    </VisuallyHidden>
                    <Input
                        ref={inputRef}
                        value={classroomName}
                        onChange={(e) =>
                            setClassroomName(e.currentTarget.value)
                        }
                        id="rename-class"
                    />
                    <button
                        className="classroom-name__validate"
                        onClick={() => editClassroomName(id, classroomName)}
                    >
                        <Icon path="checkmark" />
                        <VisuallyHidden>
                            {intl.formatMessage(commonMessages.submit)}
                        </VisuallyHidden>
                    </button>
                    <button
                        className="classroom-name__cancel"
                        onClick={cancelEdit}
                    >
                        <Icon path="close" size="medium" />
                        <VisuallyHidden>
                            {intl.formatMessage(commonMessages.cancel)}
                        </VisuallyHidden>
                    </button>
                </div>
            )}
            {editStatus === "saving" && (
                <div className="classroom-name--saving">
                    <FormattedMessage
                        id="dashboard-saving"
                        defaultMessage="Saving"
                    />
                    <LegacyLoader />
                </div>
            )}
        </div>
    );
};

export default ClassroomName;

const messages = defineMessages({
    renameClassLabel: {
        id: "defineClassName-inputLabel",
        defaultMessage: "Type a new name for your classroom {classroomName}.",
    },
});
